import { Card, Col, Row, Space, Tag, Tooltip, Typography } from 'antd';
import {
  Page,
  StrykerPerformanceIndicatorColor,
  StrykerPrimaryColor,
} from 'osep-react-antd';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import packageJson from '../../package.json';
import InlineMarkdown from '../components/InlineMarkdown';
import useInfraProjectConfigPage from '../hooks/query/useInfraProjectConfigsPage';
import { ConfigModel } from '../iasConfigSchema';
import findImageByProjectNameOrType from '../utils/findImageByProjectNameOrType';
import ErrorPage from './ErrorPage';
import { LockOutlined } from '@ant-design/icons';

const AddProjectsPage: React.FC = () => {
  const {
    data: projectConfigsPage,
    isLoading,
    isError,
    error,
  } = useInfraProjectConfigPage();

  const navigate = useNavigate();

  if (isError) {
    return <ErrorPage error={error} />;
  }

  const configs =
    projectConfigsPage?.projectConfigs.map((projectConfig) => ({
      projectId: projectConfig.projectId,
      config: projectConfig.content as ConfigModel,
      effectivePermissions: projectConfig.effectivePermissions,
    })) || [];

  return (
    <Page
      title="Infrastructure Provisioning"
      onBack={() => navigate('/ias')}
      isLoading={isLoading}
      footer={<Typography.Text disabled>{packageJson.version}</Typography.Text>}
    >
      <Row gutter={[16, 16]} justify={'start'}>
        {configs.map(({ projectId, config, effectivePermissions }) =>
          config.templates.map((template) => {
            const permissions = (effectivePermissions as any)[template.name];

            return (
              <Col key={template.name} xs={24} sm={24} md={12} lg={8} xl={6}>
                <Card
                  title={template.display_name}
                  hoverable={permissions.create_instance}
                  style={{
                    height: '100%',
                    position: 'relative',
                  }}
                  onClick={
                    permissions.create_instance
                      ? () => navigate(`/ias/add/${projectId}/${template.name}`)
                      : undefined
                  }
                  cover={
                    <Space style={{ textAlign: 'center' }}>
                      <div
                        style={{
                          position: 'relative',
                          display: 'inline-block',
                          marginTop: 20,
                        }}
                      >
                        <img
                          src={findImageByProjectNameOrType(template.name)}
                          alt={`${template.name} icon`}
                          style={{
                            height: 100,
                            width: 'auto',
                            opacity: !permissions.create_instance ? 0.2 : 1,
                          }}
                        />

                        {!permissions.create_instance && (
                          <LockOutlined
                            style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              fontSize: 32,
                              color: '#fff',
                              background: 'rgba(0, 0, 0, 0.8)',
                              borderRadius: '50%',
                              padding: 12,
                            }}
                          />
                        )}
                      </div>
                    </Space>
                  }
                >
                  <Space size={[1, 1]} wrap style={{ marginBottom: 18 }}>
                    {template.maintained_by && (
                      <Tooltip
                        title={'Maintained by ' + template.maintained_by}
                      >
                        <Tag
                          color={StrykerPerformanceIndicatorColor.GREEN}
                          key="osep"
                        >
                          {template.maintained_by}
                        </Tag>
                      </Tooltip>
                    )}
                    {template.stage !== 'stable' && (
                      <Tag
                        color={StrykerPrimaryColor.GRAY}
                        key={template.stage}
                      >
                        Beta
                      </Tag>
                    )}
                  </Space>
                  <Card.Meta
                    title={template.short_description}
                    description={
                      <InlineMarkdown>{template.description}</InlineMarkdown>
                    }
                  />
                </Card>
              </Col>
            );
          }),
        )}
      </Row>
    </Page>
  );
};

export default AddProjectsPage;
